// Copyright ©️ 2026 eVolve MEP, LLC

import { useMemo, useCallback } from 'react';

import { type WorkOrder, type WorkOrderPaginated, isWorkOrder, type StrongOmit, useEvolveGet } from '@evolvemep/api-ts';
import { Button, Flex } from '@mantine/core';

import { GridMenu } from 'components/Mantine/GridMenu';
import { useEvolveNavigate } from 'components/Mantine/Navigation/useEvolveNavigate';
import { isNotNil } from 'helpers/isNotNil';
import { ACTIONS_REMAINING_ON_PAGE, getActionButtonProps } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/common';
import {
  type WorkOrderAction,
  useCanEditWorkOrder,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useCanEditWorkOrder';
import { useWorkOrderStateModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/useWorkOrderStateModals';
import {
  WORK_ORDER_ACTIONS,
  type WorkOrderActionDef,
} from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/workOrderActions';
import { getWorkOrderStatusVerbiage } from 'modules/Shop/WorkOrders/WorkOrdersPage/Actions/WorkOrderRejectedModal';
import { useWorkOrderModals } from 'modules/Shop/WorkOrders/WorkOrdersPage/modalRegistries';
import { useWorkOrders } from 'modules/Shop/WorkOrders/WorkOrdersPage/useWorkOrders';

type Props<T extends WorkOrder | WorkOrderPaginated> = {
  onWorkOrderUpdated: (updatedWorkOrder: WorkOrder) => void;
  onWorkOrderActioned?: (action: WorkOrderAction) => void;
  workOrder: T;
} & (T extends WorkOrder
  ? {
      /** @defaultValue true */
      inline?: boolean;
    }
  : {
      /** @defaultValue true */
      inline?: true;
    });

export type WorkOrderActionButtonsProps = StrongOmit<Props<WorkOrderPaginated>, 'inline' | 'onWorkOrderActioned'> & {
  inline?: boolean;
  primary?: boolean;
  disabled?: boolean;
  onWorkOrderActioned: () => void;
};

/** Desktop trigger for a secondary (modal-only) action: a light button that opens the action's modal. */
const SecondaryActionButton = ({
  label,
  modal,
  workOrder,
  onWorkOrderActioned,
  disabled,
  inline,
}: WorkOrderActionButtonsProps & Pick<Extract<WorkOrderActionDef, { primary: false }>, 'label' | 'modal'>) => {
  const { openModal } = useWorkOrderModals();

  return (
    <Button
      onClick={() => openModal(modal, { workOrder, onWorkOrderActioned })}
      {...getActionButtonProps({ inline, disabled })}
    >
      {label}
    </Button>
  );
};

type WorkOrderStateActionsProps = {
  workOrder: WorkOrder;
  onWorkOrderActioned: (action: WorkOrderAction) => void;
  disabled: boolean;
};

/** The state prompts and additional actions menu, shown alongside the actions on the full work order view. */
const WorkOrderStateActions = ({ workOrder, onWorkOrderActioned, disabled }: WorkOrderStateActionsProps) => {
  const { openModal } = useWorkOrderModals();
  const { canViewCancellationRequest } = useWorkOrderStateModals({ workOrder, onWorkOrderActioned });
  const rejectionVerbiage = getWorkOrderStatusVerbiage(workOrder.workOrderStateName);

  return (
    <>
      {isNotNil(rejectionVerbiage) && (
        <Button variant="light" onClick={() => openModal('workOrderRejectedModal', { workOrder })}>
          View {rejectionVerbiage.progressive}
        </Button>
      )}
      {canViewCancellationRequest && (
        <Button
          variant="light"
          onClick={() => openModal('cancelWorkOrderRequestedModal', { workOrder, onWorkOrderActioned })}
          disabled={disabled}
        >
          View cancellation request
        </Button>
      )}
      <GridMenu
        options={[
          {
            label: 'Print work order traveler',
            value: 'traveler',
          },
        ]}
        variant="light"
        size="input-sm"
        iconSize="md"
        onMenuItemSelected={() => {
          openModal('workOrderTravelerModal', { workOrder });
        }}
      />
    </>
  );
};

export const WorkOrderActionButtons = <T extends WorkOrder | WorkOrderPaginated>({
  workOrder,
  onWorkOrderUpdated,
  onWorkOrderActioned: onWorkOrderActionedParent,
  inline = true,
}: Props<T>) => {
  const navigate = useEvolveNavigate(workOrder.name, true);
  const { canEditWorkOrder } = useCanEditWorkOrder(workOrder);

  const { mutationRunning } = useWorkOrders();

  const buttonProps = useMemo(
    () => ({
      workOrder,
      onWorkOrderUpdated,
      disabled: mutationRunning,
      inline,
    }),
    [workOrder, onWorkOrderUpdated, mutationRunning, inline],
  );

  const { apiCall: getWorkOrder } = useEvolveGet(`shop/workOrder/${workOrder.workOrderId}`);

  const onWorkOrderActioned = useCallback(
    async (action: WorkOrderAction, remainOnPage = false) => {
      onWorkOrderActionedParent?.(action);
      if (remainOnPage) {
        await getWorkOrder().then(onWorkOrderUpdated);
      } else {
        void navigate('/work-orders');
      }
    },
    [getWorkOrder, navigate, onWorkOrderUpdated, onWorkOrderActionedParent],
  );

  return (
    <Flex gap="sm" align="center" flex={inline ? 1 : 0} onClick={(e) => e.stopPropagation()}>
      {WORK_ORDER_ACTIONS.map((entry) => {
        if (!canEditWorkOrder(entry.action) || (inline && !entry.primary)) return null;
        const onActioned = async () =>
          onWorkOrderActioned(entry.action, ACTIONS_REMAINING_ON_PAGE.includes(entry.action));
        return entry.primary ? (
          <entry.Component key={entry.action} primary onWorkOrderActioned={onActioned} {...buttonProps} />
        ) : (
          <SecondaryActionButton
            key={entry.action}
            label={entry.label}
            modal={entry.modal}
            onWorkOrderActioned={onActioned}
            {...buttonProps}
          />
        );
      })}

      {!inline && isWorkOrder(workOrder) && (
        <WorkOrderStateActions
          workOrder={workOrder}
          onWorkOrderActioned={(action) => onWorkOrderActioned(action, ACTIONS_REMAINING_ON_PAGE.includes(action))}
          disabled={mutationRunning}
        />
      )}
    </Flex>
  );
};
